import { type BMCalendarRef } from "@traveloka/district-bloom";
import { startOfDay } from "date-fns";
import { useCallback, useMemo, useRef, useState } from "react";
import { useLocalizedDateFormat } from "../../../../core";
import { clampDate } from "../../../../utils/date";

export type DatePairTab = "from" | "to";

export type UseDatePairPickerArgs = {
  value: [Date | undefined, Date | undefined];
  onChange: (value: [Date, Date | undefined]) => void;
  minDate?: Date;
  maxDate?: Date;
};

export function useDatePairPicker({
  value: [start, end],
  onChange,
  minDate: min,
  maxDate: max,
}: UseDatePairPickerArgs) {
  const { format: formatDate } = useLocalizedDateFormat();

  const today = useRef(startOfDay(new Date())).current;
  const calendarRef = useRef<BMCalendarRef>(null);
  const [tab, setTab] = useState<DatePairTab>("from");
  const [displayedMonthYear, setDisplayedMonthYear] = useState<Date>(
    () => start ?? today
  );

  const minDate = useMemo(() => startOfDay(min ?? today), [min, today]);
  const maxDate = useMemo(
    () => startOfDay(max ?? new Date(today.getFullYear() + 5, 11, 31)),
    [max, today]
  );
  const monthOptions = useMemo(() => {
    const yr = displayedMonthYear.getFullYear();
    const minMonth = min && yr === min.getFullYear() ? min.getMonth() : 0;
    const maxMonth = max && yr === max.getFullYear() ? max.getMonth() : 11;
    return Array.from({ length: maxMonth - minMonth + 1 }, (_, i) => {
      const idx = minMonth + i;
      return {
        label: formatDate(new Date(2000, idx), "FULL_MONTH_ONLY"),
        value: String(idx),
      };
    });
  }, [formatDate, min, max, displayedMonthYear]);

  const yearOptions = useMemo(() => {
    const startYear = (min ?? today).getFullYear();
    const endYear = (max ?? new Date(today.getFullYear() + 5, 0)).getFullYear();
    return Array.from(
      { length: endYear - startYear + 1 },
      (_, i) => startYear + i
    ).map((yr) => ({ label: String(yr), value: String(yr) }));
  }, [min, max, today]);

  const display: [Date, Date] = useMemo(
    () => [start ?? today, end ?? start ?? today],
    [start, end, today]
  );

  const goToMonth = useCallback((date: Date) => {
    setDisplayedMonthYear(date);
    calendarRef.current?.scrollToMonth(date.getFullYear(), date.getMonth());
  }, []);

  const onChangeDay = useCallback(
    (dates: [Date, Date | undefined]) => {
      onChange(dates);
      if (tab === "from") {
        const [_start] = dates;
        goToMonth(end ?? _start);
        setTab("to");
        return;
      }
      const [_start, _end] = dates;
      if (!_end) goToMonth(end ?? _start);
    },
    [onChange, end, goToMonth, tab]
  );

  const onChangeTab = useCallback(
    (tab: DatePairTab) => {
      setTab(tab);
      goToMonth(tab === "from" ? (start ?? today) : (end ?? start ?? today));
    },
    [start, end, today, goToMonth]
  );

  const onChangeMonth = useCallback(
    (date: Date) => {
      setDisplayedMonthYear(clampDate(date, minDate, maxDate));
    },
    [minDate, maxDate]
  );

  return {
    tab,
    display,
    calendarRef,
    calendar: {
      minDate,
      maxDate,
      yearOptions,
      monthOptions,
      displayMonth: displayedMonthYear.getMonth(),
      displayYear: displayedMonthYear.getFullYear(),
    },
    onChangeDay,
    onChangeTab,
    onChangeMonth,
    goToMonth,
  };
}
